
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellDot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNotifications } from '@/contexts/NotificationContext';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from '@/utils/toast';

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead,
    loading,
    error 
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [newNotificationArrived, setNewNotificationArrived] = useState(false);

  // Get recent notifications (last 5)
  const recentNotifications = notifications
    .slice(0, 5)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Track when notifications arrive
  useEffect(() => {
    if (unreadCount > 0) {
      setIsVisible(true);
      setNewNotificationArrived(true);
      
      // Pulse animation for 3 seconds
      const timer = setTimeout(() => {
        setNewNotificationArrived(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    } else {
      setNewNotificationArrived(false);
    }
  }, [unreadCount, notifications]);

  // Suppress error toast and handle loading silently
  useEffect(() => {
    if (error) {
      console.error('Notification error:', error);
      // Silent error handling - don't show toast to user
    }
  }, [error]);

  const handleNotificationClick = async (id: string, link?: string) => {
    try {
      await markAsRead(id);
      setOpen(false);
      if (link) {
        navigate(link);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Silently handle error without showing toast
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  return (
    <AnimatePresence>
      {(isVisible || unreadCount > 0 || !loading) && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
        >
          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={cn(
                  "relative",
                  newNotificationArrived && "animate-pulse"
                )}
              >
                {unreadCount > 0 ? (
                  <>
                    <BellDot className="h-5 w-5" />
                    <Badge 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 border-2 border-background"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  </>
                ) : (
                  <Bell className="h-5 w-5" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={cn(
              "w-80 max-h-96 overflow-y-auto",
              isDark ? "bg-[#151830] border-slate-700/30" : "bg-white"
            )}>
              <div className="flex items-center justify-between p-4">
                <h3 className="font-medium">Notifications</h3>
                {unreadCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={markAllAsRead}
                    className="text-xs"
                  >
                    Mark all as read
                  </Button>
                )}
              </div>
              
              {loading ? (
                <div className="p-6 text-center">
                  <div className="w-6 h-6 border-2 border-t-transparent border-primary rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-muted-foreground text-sm">Loading notifications...</p>
                </div>
              ) : recentNotifications.length > 0 ? (
                <>
                  {recentNotifications.map((notification) => (
                    <div key={notification.id}>
                      <DropdownMenuItem 
                        className={cn(
                          "p-4 cursor-pointer",
                          !notification.isRead && (isDark ? "bg-[#22284a]/50" : "bg-blue-50")
                        )}
                        onClick={() => handleNotificationClick(notification.id, notification.link)}
                      >
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-start justify-between">
                            <span className="font-medium">{notification.title}</span>
                            {!notification.isRead && (
                              <span className="h-2 w-2 rounded-full bg-blue-500 mt-1"></span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{notification.message}</p>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </div>
                  ))}
                  
                  <DropdownMenuItem onClick={handleViewAll} className="p-3 text-center cursor-pointer">
                    <span className="w-full text-sm font-medium">View All Notifications</span>
                  </DropdownMenuItem>
                </>
              ) : (
                <div className="p-6 text-center">
                  <p className="text-muted-foreground">No notifications yet</p>
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationBell;
